import * as yup from 'yup';
import { useState } from 'react';
import { Formik } from 'formik';
import { Box } from '@mui/material';
import { DropDown, ChipInput, ChipInputTagErrors } from '@qrios/frontend-library';

import { FORM_INPUT_ERROR_MSGS } from '../../../../../../constants/errors';
import DateRangePicker from '../../../../../common/fields/date-range-picker';
import { WithFilterFormLayout, FilterFormProps } from '../../../../../common/filters';
import { FILTERS, BILLING_STATUSES, PAGE_SIZES } from '../../../../../common/filters/constants';
import {
  billingEndDateValidation,
  merchantCodeValidation,
  msisdnValidationSimple
} from '../../../../../../constants/validation';

import locales from '../locales';
import { MerchantPaymentBillingFilters } from './interfaces';

import { activeFiltersCountMapper, initFilters } from './filters';

const validationSchema = yup.object().shape({
  from: yup.date().required().typeError(FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('from date')),
  to: billingEndDateValidation,
  merchantCode: yup.array().of(merchantCodeValidation),
  customer_msisdn: yup.array(msisdnValidationSimple)
});

const FilterForm = ({ onSubmit, onClose, activeFilters }: FilterFormProps<MerchantPaymentBillingFilters>) => {
  const initialFiltersDifferentThanActiveFilters = activeFiltersCountMapper(activeFilters) !== 0;
  const [filtersChanged, setFilterChanged] = useState<boolean>(initialFiltersDifferentThanActiveFilters);
  const [initialValues, setInitialValues] = useState<MerchantPaymentBillingFilters>({
    from: activeFilters.from,
    to: activeFilters.to,
    operation_id: activeFilters.operation_id,
    status: activeFilters.status,
    merchant_code: activeFilters.merchant_code,
    customer_msisdn: activeFilters.customer_msisdn,
    page_size: activeFilters.page_size
  });

  return (
    <Box>
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={(filters: MerchantPaymentBillingFilters) => {
          onSubmit({
            ...filters,
            customer_msisdn: filters.customer_msisdn
          });
        }}
      >
        {({ values, handleSubmit, setFieldValue, errors, touched, handleBlur }) => {
          const handleChange = (field: string, value: unknown) => {
            setFieldValue(field, value);
            setFilterChanged(true);
          };

          const closeOrClearFilters = () => {
            if (!filtersChanged) {
              onClose();
            }
            setInitialValues(initFilters);
            setFilterChanged(false);
          };
          return (
            <WithFilterFormLayout
              onSecondary={closeOrClearFilters}
              onSubmit={handleSubmit}
              filtersChanged={filtersChanged}
            >
              <DateRangePicker
                from={{ name: FILTERS.FROM, value: values.from, error: errors.from, touched: touched.from }}
                to={{ name: FILTERS.TO, value: values.to, error: errors.to, touched: touched.to }}
                handleFromChange={value => handleChange(FILTERS.FROM, value)}
                handleToChange={value => handleChange(FILTERS.TO, value)}
                handleBlur={handleBlur}
                variant="nowEnabled"
              />
              <DropDown
                label={locales.common.filters.statuses.label}
                value={values.status}
                options={BILLING_STATUSES}
                name={FILTERS.STATUS}
                handleChange={handleChange}
                handleBlur={handleBlur}
                multiple={true}
                placeholder={locales.common.filters.statuses.placeholder}
              />
              <ChipInput
                error={errors.operation_id?.[(errors.operation_id ?? []).length - 1]}
                label={locales.common.filters.operationIds.label}
                value={values.operation_id}
                name={FILTERS.OPERATION_ID}
                handleChange={handleChange}
                handleBlur={handleBlur}
                placeholder={locales.common.filters.operationIds.placeholder}
                tagErrors={errors.operation_id as ChipInputTagErrors}
              />
              <ChipInput
                error={errors.merchant_code?.[(errors.merchant_code ?? []).length - 1]}
                label={locales.common.filters.merchant_code.label}
                value={values.merchant_code}
                name={FILTERS.MERCHANT_CODE}
                handleChange={handleChange}
                handleBlur={handleBlur}
                placeholder={locales.common.filters.merchant_code.placeholder}
                tagErrors={errors.merchant_code as ChipInputTagErrors}
              />
              <ChipInput
                name={FILTERS.CUSTOMER_MSISDN}
                label={locales.common.filters.customerMSISDN.label}
                placeholder={locales.common.filters.customerMSISDN.placeholder}
                handleBlur={handleBlur}
                handleChange={handleChange}
                error={errors.customer_msisdn?.[(errors.customer_msisdn ?? []).length - 1]}
                value={values.customer_msisdn}
                tagErrors={errors.customer_msisdn as ChipInputTagErrors}
              />
              <DropDown
                label={locales.common.filters.resultsNumber.label}
                value={values.page_size}
                options={PAGE_SIZES}
                name={FILTERS.PAGE_SIZE}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disableClearable={true}
              />
            </WithFilterFormLayout>
          );
        }}
      </Formik>
    </Box>
  );
};

export default FilterForm;
